import { Color, Spacing } from '@persona-id/design-tokens';
import { capitalize } from 'lodash/fp';
import styled from 'styled-components';

import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';
import { BASE } from 'design-system/variables/spacing';
import { intersperse } from 'lib/utils/array';

const StyledModalBody = styled(ModalBody)`
  padding-bottom: ${Spacing[6]};
`;

type Shortcut = {
  label: string;
  value: string[];
};

export type ShortcutCategory = {
  label?: string;
  shortcuts: Shortcut[];
};

interface ShortcutsModalProps {
  shortcuts: ShortcutCategory[];
  handleClose: () => void;
}

const ShortcutsModal = ({ shortcuts, handleClose }: ShortcutsModalProps) => (
  <ModalContainer>
    <ModalHeader onClose={handleClose}>Keyboard shortcuts</ModalHeader>
    <StyledModalBody>
      {shortcuts.map((section, i) => {
        if (section?.label == null) return null;
        const { label, shortcuts } = section;
        return <ShortcutSection key={`${label}-${i}`} title={label} shortcuts={shortcuts} />;
      })}
    </StyledModalBody>
  </ModalContainer>
);

const RowWrapper = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-row-gap: 10px;
  :not(:last-child) {
    margin-bottom: ${Spacing[5]};
  }
`;

const ShortcutSection = ({ title, shortcuts }: { title: string; shortcuts: Shortcut[] }) => {
  return (
    <>
      <Card.SectionHeader>{capitalize(title)}</Card.SectionHeader>
      <RowWrapper>
        {shortcuts.map((shortcut, i) => (
          <ShortcutRow
            key={`${shortcut.label}-${i}`}
            title={shortcut.label}
            keys={shortcut.value}
          />
        ))}
      </RowWrapper>
    </>
  );
};

const Row = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ShortcutLabel = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const KeysWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  grid-column-gap: ${Spacing[1]};
  margin-left: auto;
`;

const KeyTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${BASE * 3.5}px;
  min-width: ${BASE * 3.5}px;
  padding: ${Spacing[1]};
  background-color: ${Color.CoreSlate800};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const KeyLabel = styled(Paragraph).attrs(() => ({ size: 'lg' }))`
  color: ${({ theme }) => theme.color.primary.white};
  font-weight: 500;

  // removes the extra height on whitespace above and below character
  font-size: 18px;
  height: 14px;
  line-height: 16px;
`;

const ShortcutRow = ({ title, keys }: { title: string; keys: string[] }) => {
  return (
    <Row>
      <ShortcutLabel>{capitalize(title)}</ShortcutLabel>
      <KeysWrapper>
        {intersperse(
          keys.map((shortcutKey, i) => (
            <KeyTile key={`${title}-${shortcutKey}-${i}`}>
              <KeyLabel>{capitalize(shortcutKey)}</KeyLabel>
            </KeyTile>
          )),
          (i) => (
            <span key={i}>+</span>
          ),
        )}
      </KeysWrapper>
    </Row>
  );
};

export default ShortcutsModal;
